/**
 * Texas weather regions (DESIGN §3.3). Intensities are on the ES scale
 * (CA CZ 13 Fresno cooling = 1.65).
 */
import type { RegionId, TduId } from "./types";

export interface RegionConfig {
  readonly id: RegionId;
  readonly name: string;
  /** Representative latitude, degrees N. */
  readonly latitude: number;
  /** Relative cooling intensity (ES scale). */
  readonly coolingIntensity: number;
  /** Relative heating intensity (ES scale). */
  readonly heatingIntensity: number;
  /** Relative solar clearness (ES scale, 1.0 = LA coastal). */
  readonly solarClearness: number;
  /** Default specific yield, kWh per kWdc per year. */
  readonly defaultYieldKwhPerKw: number;
  /** TDUs that serve this region. */
  readonly tdus: readonly TduId[];
  /** Example cities, for the rep's region picker. */
  readonly examples: string;
  readonly effectiveDate: string;
  readonly source: string;
}

const SRC = "DESIGN §3.3 (TX estimates on the ES intensity scale)";

export const REGIONS: Readonly<Record<RegionId, RegionConfig>> = {
  dfw: {
    id: "dfw",
    name: "North Texas (DFW)",
    latitude: 32.8,
    coolingIntensity: 1.6,
    heatingIntensity: 0.9,
    solarClearness: 1.04,
    defaultYieldKwhPerKw: 1450,
    tdus: ["oncor", "tnmp"],
    examples: "Dallas, Fort Worth, Lewisville (TNMP)",
    effectiveDate: "2026-10-06",
    source: SRC,
  },
  central: {
    id: "central",
    name: "Central Texas",
    latitude: 31.5,
    coolingIntensity: 1.65,
    heatingIntensity: 0.8,
    solarClearness: 1.05,
    defaultYieldKwhPerKw: 1475,
    tdus: ["oncor", "tnmp"],
    examples: "Waco, Killeen, Clifton / Glen Rose (TNMP)",
    effectiveDate: "2026-10-06",
    source: SRC,
  },
  gulf: {
    id: "gulf",
    name: "Houston / Gulf Coast",
    latitude: 29.8,
    coolingIntensity: 1.75,
    heatingIntensity: 0.55,
    solarClearness: 0.98,
    defaultYieldKwhPerKw: 1350,
    tdus: ["centerpoint", "tnmp"],
    examples: "Houston, League City / Texas City / Friendswood (TNMP)",
    effectiveDate: "2026-10-06",
    source: SRC,
  },
  west: {
    id: "west",
    name: "West Texas",
    latitude: 31.9,
    coolingIntensity: 1.7,
    heatingIntensity: 0.85,
    solarClearness: 1.15,
    defaultYieldKwhPerKw: 1650,
    tdus: ["oncor", "tnmp"],
    examples: "Midland / Odessa (Oncor), Pecos / Fort Stockton (TNMP)",
    effectiveDate: "2026-10-06",
    source: SRC,
  },
};

export const REGION_IDS: readonly RegionId[] = ["dfw", "central", "gulf", "west"] as const;

/** Regions valid for a TDU, in display order. */
export function regionsForTdu(tdu: TduId): RegionConfig[] {
  return REGION_IDS.map((id) => REGIONS[id]).filter((r) => r.tdus.includes(tdu));
}

/** True if the region is served by the TDU. */
export function isValidRegionForTdu(tdu: TduId, region: RegionId): boolean {
  return REGIONS[region].tdus.includes(tdu);
}
